const ChemicalRename = () => (
  <div>
    <div>
      <h1 className="blog-title">
        Harmonizing UK Biobank GWAS Data with DISGENET: Mapping Phecodes to UMLS CUIs
      </h1>
      <p className="blog-date">
        Posted on <strong>Apr 23, 2025</strong>
      </p>
      <p className="blog-paragraph">
        UK Biobank data is an invaluable resource for genetic research, containing genetic, lifestyle, and
        health information from half a million individuals. To leverage this wealth of data,{' '}
        <a href="https://disgenet.com/DISGENET-Version-25-1-Whats-New" target="blank">
          DISGENET v25.1
        </a>{' '}
        features the integration of a dataset that provides genome-wide associations for Electronic Health
        Record (EHR)-derived phenotypes from White British participants in the UK Biobank (data available at
        <a href="https://pheweb.org/UKB-TOPMed/about" target="blank">
          {' '}
          PheWeb UKB-TOPMed
        </a>
        ).
      </p>
      <p className="blog-paragraph">
        However, while integrating these data, a significant challenge emerged: aligning the 1,419 phenotypes
        encoded using Phecodes with the DISGENET coding system, which relies on Unified Medical Language
        System (UMLS) Concept Unique Identifiers (CUIs).
      </p>
    </div>
    <div>
      <h2 className="blog-subtitle">What are Phecodes?</h2>
      <p className="blog-paragraph">
        When conducting a genome-wide association study (GWAS) using UK Biobank data, one of the key
        challenges is harmonizing phenotype definitions. Phecodes help address the harmonization of different
        phenotype definitions by grouping ICD (International Classification of Diseases) codes into clinically
        meaningful categories, enhancing the statistical power of both GWAS and Phenome-Wide Association
        Studies (PheWAS).
      </p>
      <img
        src="static/images/blog/9_GWAS-PheWAS.jpg"
        alt="GWAS/PheWAS - img -"
        style={{ height: '600px', width: 'auto', display: 'block', margin: 'auto' }}
      />
      <p className="blog-paragraph">
        Initially developed for Phenome-Wide Association Studies to facilitate the exploration of genetic
        associations across multiple phenotypes, Phecodes now play a crucial role in EHR phenotyping. Their
        ability to aggregate similar conditions into broader disease categories simplifies large-scale genetic
        analyses and prevents phenotypic data fragmentation.
      </p>
      <p className="blog-paragraph">
        Phecodes are maintained by the Center for Precision Medicine at Vanderbilt University Medical Center
        and are available in the{' '}
        <a href="https://phewascatalog.org/phewas/#phe12" target="blank">
          {' '}
          PheWAS Catalog
        </a>
        . The latest version (1.2) includes 1,867 Phecodes, derived from approximately 15,500 ICD-9-CM codes
        and 90,000 ICD-10-CM codes.
      </p>
    </div>
    <div>
      <h2 className="blog-subtitle">Challenges in Mapping Phecodes to UMLS CUIs</h2>
      <p className="blog-paragraph">
        Phecodes are hierarchical, capturing disease phenotypes at multiple levels:
      </p>
      <ul className="blog-list">
        <li className="blog-paragraph">
          Three-digit Phecodes (e.g., 301 - Personality disorders) represent broad phenotypes.
        </li>
        <li className="blog-paragraph">
          More specific Phecodes (e.g., 301.1 - Affective personality disorder, 301.12 - Chronic depressive
          personality disorder) refine the classification.
        </li>
      </ul>
      <br />
      <img
        src="static/images/blog/9_Personality disorders.jpg"
        alt="Personality disorders - img -"
        style={{
          height: '450px',
          width: 'auto',
          display: 'block',
          margin: 'auto',
        }}
      />
      <br />
      <p className="blog-paragraph">
        A single Phecode often corresponds to one or several ICD codes. However, in some cases, a single
        Phecode can represent over 100 different ICD codes. This wide variation presents a significant
        challenge when combining Phecode-based data with datasets featuring other clinical coding systems or
        disease ontologies.
      </p>
      <p className="blog-paragraph">
        Because of this multiplicity, to integrate this dataset with other data in DISGENET, coded using UMLS
        CUIs, we decided to find a way of mapping the Phecodes to one or a few concepts that best capture each
        Phecode. The alternative approach of mapping Phecodes to all their corresponding ICD codes and
        subsequently using UMLS cross-references to retrieve the CUIs would result in redundant
        representations of genetic associations within DISGENET, potentially inflating the data and
        introducing biases in downstream analyses.
      </p>
    </div>
    <div>
      <h2 className="blog-subtitle">Our Approach: Mapping Phecodes to CUIs</h2>
      <p className="blog-paragraph">
        Rather than converting Phecodes to ICDs first and then mapping to CUIs, we developed a direct mapping
        strategy to associate each Phecode with one or two CUIs that best capture its meaning. UMLS CUIs serve
        as standardized references within the UMLS, enabling integration across multiple biomedical datasets
        and ensuring uniformity in disease representation.
      </p>
      <br />
      <img
        src="static/images/blog/9_Mapping.png"
        alt="Mapping from Phecodes to UMLS Concepts - img -"
        style={{ height: 'auto', width: '90%', display: 'block', margin: 'auto' }}
      />
      <br />
      <div>
        <h5 className="blog-sub-subtitle">Our 4-step strategy was as follows:</h5>
        <ul className="blog-list">
          <ul className="blog-paragraph">
            <strong>1. Mapping by Single ICD-9:</strong> If a Phecode maps exclusively to one ICD-9 code, we
            use it to map the Phecode to a UMLS CUI via UMLS cross-references.{' '}
          </ul>
          <ul className="blog-paragraph">
            <strong>2. Mapping by Single ICD-10:</strong> If a Phecode maps exclusively to one ICD-10 code, we
            use it to map the Phecode to a UMLS CUI via UMLS cross-references.{' '}
          </ul>
          <ul className="blog-paragraph">
            <strong>3. Strict Matching by Phecode Name:</strong> If a Phecode maps to multiple ICD codes, we
            look for an exact match between the Phecode name (PhecodeString) and a CUI name from the MRCONSO
            (UMLS Meta-thesaurus Concept Names and Sources) file. If multiple matches exist, we keep the CUI
            associated with the largest number of genes in DISGENET.{' '}
          </ul>
          <ul className="blog-paragraph">
            <strong>4. Similarity-Based Matching by Phecode Name:</strong> When multiple ICD codes are linked
            to a Phecode, and no concept name matches exactly the PhecodeString, we apply a string
            similarity-based algorithm to find the best match from the UMLS MRCONSO concept names, followed by
            expert review for lower-confidence cases.{' '}
          </ul>
        </ul>
      </div>
    </div>
    <div>
      <h2 className="blog-subtitle">Key Takeaways from Phecode Mapping</h2>
      <div>
        <img
          src="static/images/blog/9_Key takeaways.jpg"
          alt="Key Takeaways from Phecode Mapping - img -"
          style={{ height: '470px', width: 'auto', display: 'block', margin: 'auto' }}
        />
        <h6 className="blog-sub-subtitle" style={{ marginTop: '30px' }}>
          Single ICD Code Mapping
        </h6>
        <p className="blog-list blog-paragraph">
          30% of Phecodes had a single ICD-9 or ICD-10 code which we used to map to CUIs.
        </p>
      </div>
      <div>
        <h6 className="blog-sub-subtitle" style={{ marginTop: '30px' }}>
          Exact Name Matches
        </h6>
        <p className="blog-list blog-paragraph">
          Over 50% of Phecodes had names that exactly matched one CUI.
        </p>
      </div>
      <div>
        <h6 className="blog-sub-subtitle" style={{ marginTop: '30px' }}>
          Similar Names Matches
        </h6>
        <p className="blog-list blog-paragraph">
          Expert curation was necessary to identify the more appropriate CUI for 20% of Phecodes.
        </p>
      </div>
      <div>
        <h6 className="blog-sub-subtitle" style={{ marginTop: '30px' }}>
          Multiple CUIs for Some Phecodes
        </h6>
        <p className="blog-list blog-paragraph">
          Some Phecodes mapped to multiple CUIs. In these cases, we keep the one with information in the
          DISGENET database.
        </p>
      </div>
      <div>
        <h6 className="blog-sub-subtitle" style={{ marginTop: '30px' }}>
          Expert Review Ensured Accuracy
        </h6>
        <p className="blog-list blog-paragraph">
          After expert review of the mappings using the similarity-based approach, all Phecodes could be
          mapped to CUIs.
        </p>
      </div>
      <div>
        <h6 className="blog-sub-subtitle" style={{ marginTop: '30px' }}>
          Mapping to Two CUIs
        </h6>
        <p className="blog-list blog-paragraph">
          In the final dataset, only 5 Phecodes required mapping to two CUIs, mainly due to conditions
          affecting different anatomical sites (e.g., 681.2 - Cellulitis and abscess of face/neck → C0157696;
          C0157697, Cellulitis and abscess of face; Cellulitis and abscess of neck).
        </p>
      </div>
      <div>
        <h6 className="blog-sub-subtitle" style={{ marginTop: '30px' }}>
          Complex Mapping for One Phecode
        </h6>
        <p className="blog-list blog-paragraph" style={{ marginBottom: '20px' }}>
          Only one phecode (225.1 - Benign neoplasm of brain, cranial nerves, meninges) mapped to three CUIs
          due to the same reason (C0004992, C0154033, C0496899. Benign neoplasm of cranial nerves; Benign
          neoplasm of cerebral meninges; Benign neoplasm of brain).
        </p>
      </div>
      <h3 className="blog-subtitle">Challenges in Mapping Phecodes for GWAS</h3>
      <div>
        <h6 className="blog-sub-subtitle" style={{ marginTop: '30px' }}>
          Need for Expert Review
        </h6>
        <p className="blog-paragraph blog-list">
          Expert review was necessary to ensure accurate and less redundant mapping.
        </p>
      </div>
      <div>
        <h6 className="blog-sub-subtitle" style={{ marginTop: '30px' }}>
          Limitations of Large Language Models (LLMs)
        </h6>
        <p className="blog-paragraph blog-list">
          Automating this process with Large Language Models (LLMs) was not feasible, as they perform poorly
          in entity normalization.
        </p>
      </div>
      <div>
        <h6 className="blog-sub-subtitle" style={{ marginTop: '30px' }}>
          Granularity of UMLS CUIs
        </h6>
        <p className="blog-paragraph blog-list" style={{ marginBottom: '20px' }}>
          UMLS CUIs offer the granularity needed for this task, as they support both specific disease terms
          and broader hierarchical navigation, aligning well with phecode structures.
        </p>
      </div>
    </div>
    <div>
      <h2 className="blog-subtitle">Advancing GWAS Integration in DISGENET</h2>
      <p className="blog-paragraph">
        This Phecode-to-CUI mapping will enable researchers working with UK Biobank GWAS data to align their
        findings with other datasets integrated into DISGENET, enhancing the utility and interpretability of
        genetic discoveries.
      </p>
      <p className="blog-paragraph">
        By directly linking Phecodes to CUIs, we improve disease concept harmonization, ensuring that GWAS
        insights can be integrated with broader biomedical knowledge resources.
      </p>
      <p className="blog-paragraph">
        Notably, the implemented mapping procedure is designed for reusability, enabling the integration of
        diverse EHR-derived phenotype datasets.
      </p>
    </div>
    <div style={{ marginLeft: '50px' }}>
      <br />
      <li>
        <a href="https://disgenet.com/free-trial-apply" target="blank">
          {' '}
          Start a 7-day free trial
        </a>
      </li>
      <li>
        <a href="https://calendly.com/medbioinformatics/disgenet-demo?month=2025-04" target="blank">
          {' '}
          Book a demo for your company
        </a>
      </li>
    </div>
  </div>
);

export { ChemicalRename };
